interface SlackErrorReport {
  error: {
    message: string;
    stack?: string;
    type?: string;
  };
  context: {
    endpoint: string;
    timestamp?: string;
    errorId?: string;
    environment?: string;
  };
  leadData?: {
    contactInfo?: {
      name?: string;
      phone?: string;
      email?: string;
    };
    propertyInfo?: {
      streetName?: string;
      houseNumber?: string;
      city?: string;
      zipCode?: string;
      propertyType?: string;
      livingArea?: number;
      bedrooms?: number;
      bathrooms?: number;
      priceRange?: { min: number; max: number };
    };
    sellingTimeframe?: string;
    additionalDetails?: string;
  };
  userData?: {
    userId?: string;
    guestId?: string;
    chatId?: string;
    agencyId?: string;
  };
  systemContext?: {
    databaseConnected?: boolean;
    agencyFound?: boolean;
    azureGraphAvailable?: boolean;
  };
  requestPayload?: any;
  responseData?: any;
}

/**
 * Send an error report to Slack
 * This is a fire-and-forget operation that won't block the main flow
 */
export async function sendErrorToSlack(errorReport: SlackErrorReport): Promise<void> {
  try {
    // Generate error ID if not provided
    if (!errorReport.context.errorId) {
      errorReport.context.errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    // Set timestamp if not provided
    if (!errorReport.context.timestamp) {
      errorReport.context.timestamp = new Date().toISOString();
    }
    
    // Set environment if not provided
    if (!errorReport.context.environment) {
      errorReport.context.environment = process.env.NODE_ENV || 'development';
    }

    // Fire-and-forget call to our Slack endpoint
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000';
    fetch(`${baseUrl}/api/slack/error-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorReport)
    }).catch(err => {
      console.error('Failed to send error report to Slack:', err);
    });

    console.log(`Error report queued for Slack: ${errorReport.context.errorId}`);
  } catch (error) {
    console.error('Error in sendErrorToSlack:', error);
  }
}

/**
 * Helper function to create error report for lead creation failures
 */
export function createLeadErrorReport(
  error: Error,
  endpoint: string,
  leadData?: any,
  userData?: any,
  systemContext?: any,
  requestPayload?: any
): SlackErrorReport {
  return {
    error: {
      message: error.message,
      stack: error.stack,
      type: error.constructor.name
    },
    context: {
      endpoint,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    },
    leadData: leadData ? {
      contactInfo: leadData.contactInfo,
      propertyInfo: {
        streetName: leadData.streetName,
        houseNumber: leadData.houseNumber,
        city: leadData.city,
        zipCode: leadData.zipCode,
        propertyType: leadData.propertyType,
        livingArea: leadData.propertyDetails?.livingArea,
        bedrooms: leadData.propertyDetails?.bedrooms,
        bathrooms: leadData.propertyDetails?.bathrooms,
        priceRange: leadData.priceRange
      },
      sellingTimeframe: leadData.sellingTimeframe,
      additionalDetails: leadData.additionalDetails
    } : undefined,
    userData: userData ? {
      userId: userData.userId,
      guestId: userData.guestId,
      chatId: userData.chatId,
      agencyId: userData.agencyId
    } : undefined,
    systemContext,
    requestPayload
  };
}

/**
 * Helper function to create error report for user creation failures
 */
export function createUserErrorReport(
  error: Error,
  endpoint: string,
  userData?: any,
  requestPayload?: any
): SlackErrorReport {
  return {
    error: {
      message: error.message,
      stack: error.stack,
      type: error.constructor.name
    },
    context: {
      endpoint,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    },
    userData: userData ? {
      userId: userData.userId,
      guestId: userData.guestId,
      chatId: userData.chatId,
      agencyId: userData.agencyId
    } : undefined,
    requestPayload
  };
}

/**
 * Helper function to create error report for email sending failures
 */
export function createEmailErrorReport(
  error: Error,
  endpoint: string,
  recipientEmail: string,
  leadData?: any,
  userData?: any,
  requestPayload?: any
): SlackErrorReport {
  return {
    error: {
      message: error.message,
      stack: error.stack,
      type: error.constructor.name
    },
    context: {
      endpoint,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    },
    leadData: leadData ? {
      contactInfo: leadData.contactInfo,
      propertyInfo: {
        streetName: leadData.streetName,
        houseNumber: leadData.houseNumber,
        city: leadData.city,
        zipCode: leadData.zipCode,
        propertyType: leadData.propertyType,
        livingArea: leadData.propertyDetails?.livingArea,
        bedrooms: leadData.propertyDetails?.bedrooms,
        bathrooms: leadData.propertyDetails?.bathrooms,
        priceRange: leadData.priceRange
      },
      sellingTimeframe: leadData.sellingTimeframe,
      additionalDetails: leadData.additionalDetails
    } : undefined,
    userData: userData ? {
      userId: userData.userId,
      guestId: userData.guestId,
      chatId: userData.chatId,
      agencyId: userData.agencyId
    } : undefined,
    requestPayload: {
      ...requestPayload,
      recipientEmail
    }
  };
}

/**
 * Send WhatsApp onboarding success/failure notification to Slack
 */
export async function sendWhatsAppOnboardingNotification(
  success: boolean,
  agencyId: string,
  wabaData?: {
    wabaId?: string;
    phoneNumberId?: string;
    displayPhoneNumber?: string;
    displayName?: string;
    status?: string;
    qualityRating?: string;
    accessTokenObtained?: boolean;
    accessTokenLength?: number;
  },
  error?: string,
  requestData?: any
): Promise<void> {
  try {
    const notificationId = `whatsapp_${success ? 'success' : 'error'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000';
    fetch(`${baseUrl}/api/slack/whatsapp-onboarding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        success,
        agencyId,
        wabaData,
        error,
        requestData,
        notificationId,
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV || 'development'
      })
    }).catch(err => {
      console.error('Failed to send WhatsApp onboarding notification to Slack:', err);
    });

    console.log(`WhatsApp onboarding notification queued for Slack: ${notificationId}`);
  } catch (error) {
    console.error('Error in sendWhatsAppOnboardingNotification:', error);
  }
}
